import type {
  SavedCircuitConfigurationIdentity,
  SavedCircuitLapSet,
  TrackContribution,
} from '../types/index.js';

function finiteCoordinate(point: { lat: number; lon: number } | null | undefined) {
  return Boolean(
    point &&
    Number.isFinite(point.lat) &&
    Math.abs(point.lat) <= 90 &&
    Number.isFinite(point.lon) &&
    Math.abs(point.lon) <= 180,
  );
}

function usableStartLine(
  line:
    | { pointA: { lat: number; lon: number }; pointB: { lat: number; lon: number } }
    | null
    | undefined,
) {
  return Boolean(
    line &&
    finiteCoordinate(line.pointA) &&
    finiteCoordinate(line.pointB) &&
    (line.pointA.lat !== line.pointB.lat || line.pointA.lon !== line.pointB.lon),
  );
}

export function buildSavedCircuitConfigurationIdentity(
  contribution: TrackContribution,
  startFinishLine: SavedCircuitConfigurationIdentity['startFinishLine'] = contribution.payload
    .startLineSuggestion,
): SavedCircuitConfigurationIdentity | null {
  const contributionRevision =
    contribution.payload.detailedGeometryRevision ?? contribution.updatedAt;
  if (
    !usableStartLine(startFinishLine) ||
    !contribution.id.trim() ||
    !contribution.trackId.trim() ||
    !contribution.updatedAt.trim() ||
    !Number.isFinite(Date.parse(contribution.updatedAt)) ||
    !contributionRevision.trim()
  ) {
    return null;
  }

  return {
    version: 1,
    contributionId: contribution.id,
    contributionUpdatedAt: contribution.updatedAt,
    contributionRevision,
    trackId: contribution.trackId,
    layoutName: contribution.layoutName?.trim() || null,
    direction: contribution.payload.direction,
    startFinishLine: {
      pointA: { ...startFinishLine.pointA },
      pointB: { ...startFinishLine.pointB },
    },
  };
}

function stringField(value: unknown) {
  return typeof value === 'string' && value.trim() ? value : null;
}

function parsedSavedCircuitConfiguration(identity: unknown) {
  if (!identity || typeof identity !== 'object') {
    return null;
  }

  const candidate = identity as Partial<SavedCircuitConfigurationIdentity>;
  const contributionId = stringField(candidate.contributionId);
  const contributionUpdatedAt = stringField(candidate.contributionUpdatedAt);
  const contributionRevision = stringField(candidate.contributionRevision);
  const trackId = stringField(candidate.trackId);
  const layoutName =
    candidate.layoutName === null
      ? null
      : typeof candidate.layoutName === 'string'
        ? candidate.layoutName.trim() || null
        : undefined;
  const direction = candidate.direction;
  const startFinishLine = candidate.startFinishLine;

  if (
    candidate.version !== 1 ||
    !contributionId ||
    !contributionUpdatedAt ||
    !Number.isFinite(Date.parse(contributionUpdatedAt)) ||
    !trackId ||
    layoutName === undefined ||
    (direction !== 'clockwise' && direction !== 'counterclockwise') ||
    !usableStartLine(startFinishLine)
  ) {
    return null;
  }

  return {
    version: candidate.version,
    contributionId,
    contributionRevision,
    trackId,
    layoutName,
    direction,
    startFinishLine: startFinishLine!,
  };
}

function legacySavedCircuitConfigurationKey(identity: unknown): string | null {
  const parsed = parsedSavedCircuitConfiguration(identity);
  if (!parsed) {
    return null;
  }

  return JSON.stringify([
    parsed.version,
    parsed.contributionId,
    parsed.trackId,
    parsed.layoutName,
    parsed.direction,
    parsed.startFinishLine.pointA.lat,
    parsed.startFinishLine.pointA.lon,
    parsed.startFinishLine.pointB.lat,
    parsed.startFinishLine.pointB.lon,
  ]);
}

function hasContributionRevisionProperty(identity: unknown) {
  return Boolean(
    identity &&
    typeof identity === 'object' &&
    Object.prototype.hasOwnProperty.call(identity, 'contributionRevision'),
  );
}
/**
 * Returns the semantic identity key for derived circuit timing.
 *
 * `contributionUpdatedAt` is retained as provenance, but a stable
 * `contributionRevision` is the collision-proof invalidation boundary. The
 * parser is deliberately total so malformed persisted data fails closed.
 */
export function savedCircuitConfigurationKey(identity: unknown): string | null {
  const parsed = parsedSavedCircuitConfiguration(identity);
  if (!parsed?.contributionRevision) {
    return null;
  }

  return JSON.stringify([
    parsed.version,
    parsed.contributionId,
    parsed.contributionRevision,
    parsed.trackId,
    parsed.layoutName,
    parsed.direction,
    parsed.startFinishLine.pointA.lat,
    parsed.startFinishLine.pointA.lon,
    parsed.startFinishLine.pointB.lat,
    parsed.startFinishLine.pointB.lon,
  ]);
}

export function savedCircuitConfigurationsMatch(left: unknown, right: unknown) {
  const leftKey = savedCircuitConfigurationKey(left);
  const rightKey = savedCircuitConfigurationKey(right);
  if (leftKey || rightKey) {
    return leftKey !== null && leftKey === rightKey;
  }

  if (hasContributionRevisionProperty(left) || hasContributionRevisionProperty(right)) {
    return false;
  }
  // The ideal-line API retains its established compatibility path only when
  // both identities predate contribution revisions. Saved lap rendering and
  // draft reslices separately require the strict key above.
  const leftLegacyKey = legacySavedCircuitConfigurationKey(left);
  return leftLegacyKey !== null && leftLegacyKey === legacySavedCircuitConfigurationKey(right);
}

/**
 * Resolves the immutable configuration that produced a saved lap set.
 *
 * Legacy lap sets may use the current contribution only when its last update
 * predates the lap save. If the contribution changed later, the historical
 * layout/direction/start line cannot be proven and the lap set is ineligible.
 */
export function resolveSavedCircuitConfiguration(
  lapSet: SavedCircuitLapSet,
  contribution: TrackContribution | null,
): SavedCircuitConfigurationIdentity | null {
  if (lapSet.configuration) {
    return lapSet.configuration.contributionId === lapSet.contributionId &&
      legacySavedCircuitConfigurationKey(lapSet.configuration)
      ? lapSet.configuration
      : null;
  }

  if (!contribution || contribution.id !== lapSet.contributionId) {
    return null;
  }

  const savedAtMs = Date.parse(lapSet.savedAt);
  const contributionUpdatedAtMs = Date.parse(contribution.updatedAt);
  if (
    !Number.isFinite(savedAtMs) ||
    !Number.isFinite(contributionUpdatedAtMs) ||
    contributionUpdatedAtMs > savedAtMs
  ) {
    return null;
  }

  const inferred = buildSavedCircuitConfigurationIdentity(contribution);
  if (!inferred) {
    return null;
  }

  const legacyIdentity = { ...inferred };
  delete legacyIdentity.contributionRevision;
  return legacyIdentity;
}
